/**
 * UserTools — token-truth login/logout links for the header tools slot
 * (BUG-2026-211; pattern from CR-2026-009 / volto-polyglot).
 *
 * Replaces VLT's Anontools, which renders from state.actions.actions — a
 * session-derived cache that goes stale on all-private tenants (the
 * post-logout anonymous GET_CONTENT 401s and nothing refreshes it). The
 * session truth is state.userSession.token, which the logout chain always
 * clears.
 *
 * Divergence from the CR-2026-009 component: "Log out" links to Volto's
 * /logout route instead of hard-redirecting after manual cookie removal.
 * Model-A nick implements POST /@logout (server-side token revocation) —
 * the upgrade CR-2026-009 §4.4 anticipated. The route flow now renders
 * correctly because volto-nick-compat resets the session-derived reducers
 * on logout (sessionHygiene, same BUG).
 *
 * An expired JWT in the cookie (browser tabs persisting across sessions)
 * must not show "Log out" — show "Log in" and clear the stale cookie.
 *
 * BUG-2026-217 enhancement: when logged in, show WHO is logged in next to
 * "Log out" — read from the token itself (nick signs `fullname` + `sub`; see
 * @robgietema/nick src/routes/authentication.ts). No extra fetch: the display
 * name is already carried in the JWT that state.userSession.token holds.
 *
 * DR-2026-272: this component is the fleet's headerTools cluster and also
 * renders the GuidesLink element (the fleet Guides navigation to the
 * guides.ljusa-tider.se commons) in both auth states.
 */
import { Link, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import { toBackendLang } from '@plone/volto/helpers/Utils/Utils';
import Cookies from 'universal-cookie';
import GuidesLink from './GuidesLink';
import ReturnLink from './ReturnLink';

// Decode + validate the JWT; return its payload (fullname/sub/exp) or null.
function decodeValidToken(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return null;
    // exp is in seconds, Date.now() in milliseconds
    if (payload.exp * 1000 <= Date.now()) return null;
    return payload;
  } catch {
    return null;
  }
}

const UserTools = () => {
  const token = useSelector((state) => state.userSession?.token, shallowEqual);
  const content = useSelector((state) => state.content.data, shallowEqual);
  const location = useLocation();
  const intl = useIntl();
  const isMultilingual = useSelector(
    (state) => state.site?.data?.features?.multilingual,
  );

  // In multilingual mode, prefix login/logout with the current language so
  // Logout's return_url derivation lands back inside the languageroot.
  const langPrefix = isMultilingual ? `/${toBackendLang(intl.locale)}` : '';

  const payload = decodeValidToken(token);

  // If token exists but is expired, clear the stale cookie
  if (token && !payload) {
    const cookies = new Cookies();
    cookies.remove('auth_token', { path: '/' });
  }

  if (payload) {
    // Show WHO is logged in — the USERNAME only (payload.sub: mack/scarlett/
    // admin), per operator. Deliberately NOT fullname: usernames are ASCII so
    // there is no atob/UTF-8 mojibake (fullname "Palomäki" base64-decoded via
    // atob rendered "PalomÃ¤ki"), and the operator wants the short login id.
    const username = payload.sub;
    return (
      <span className="user-tools-session">
        <ReturnLink />
        <GuidesLink />
        {username && (
          <span
            className="user-tools-username"
            title={username}
            style={{ marginRight: '0.75em' }}
          >
            {username}
          </span>
        )}
        <Link aria-label="logout" to={`${langPrefix}/logout`}>
          <FormattedMessage id="Log out" defaultMessage="Log out" />
        </Link>
      </span>
    );
  }

  const returnUrl = content?.['@id']
    ? content['@id'].replace(/^https?:\/\/[^/]+/, '')
    : location.pathname;

  return (
    <>
      <ReturnLink />
      <GuidesLink />
      <Link
        aria-label="login"
        to={`${langPrefix}/login${returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : ''}`}
      >
        <FormattedMessage id="Log in" defaultMessage="Log in" />
      </Link>
    </>
  );
};

export default UserTools;
